import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const DISCORD_URL = process.env.REACT_APP_DISCORD_URL || 'https://discord.gg/hosting-community';

function DiscordIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}

export function Footer() {
  const { t } = useLanguage();

  return (
    <footer
      data-testid="footer"
      className="bg-[#020817] border-t border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <img
              src="/assets/logo.png"
              alt="Hosting-Community"
              className="h-10 w-auto mb-3"
            />
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-discord-btn"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[#5865F2]/15 border border-[#5865F2]/30 text-[#7289da] rounded-lg text-sm font-medium hover:bg-[#5865F2]/25 hover:border-[#5865F2]/60 transition-colors"
            >
              <DiscordIcon className="w-4 h-4" />
              Discord Community
            </a>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              {t.footer.links}
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="#vergleich"
                  data-testid="footer-compare-link"
                  className="text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('vergleich')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {t.nav.compare}
                </a>
              </li>
              <li>
                <a
                  href="/#anbieter-werden"
                  data-testid="footer-listing-link"
                  className="text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                  onClick={(e) => {
                    if (window.location.pathname === '/') {
                      e.preventDefault();
                      document.getElementById('anbieter-werden')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  {t.footer.listProvider}
                </a>
              </li>
              <li>
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-discord-link"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#7289da] transition-colors"
                >
                  <DiscordIcon className="w-3.5 h-3.5" />
                  Discord
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">
              {t.footer.contact}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/impressum"
                  data-testid="footer-imprint-link"
                  className="text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                >
                  {t.footer.imprint}
                </Link>
              </li>
              <li>
                <Link
                  to="/datenschutz"
                  data-testid="footer-privacy-link"
                  className="text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                >
                  {t.footer.privacy}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">{t.footer.copyright}</p>
          <a
            href="https://hosting-community.de"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-website-link"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-cyan-400 transition-colors"
          >
            hosting-community.de
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </footer>
  );
}
